import React, { useState } from "react";
import { Box, Button, Card, Dropdown, Grid, Icon, Table, Text } from "~/components-shared";
import styled from "styled-components";
import { usePostHog } from "posthog-js/react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import NotMovingTableRow from "~/components/insightsWidgets/NotMovingTableRow";
import StyledIcon from "~/components/StyledIcon";
import { InsightsLineItemItemStatusEnum, InsightsLineItemItemTypeEnum, RoutesEnum } from "~/types/enums";
import { useSelector } from "react-redux";
import topicSelectors from "~/store/topics/selectors";
const StyledTable = styled(Table)`
  width: ${_ref => {
  let {
    theme
  } = _ref;
  return `calc(100% + ${10 * theme.typography.fontStep}rem)`;
}};
`;
const StyledTableHeader = styled(Table.Header)`
  border-bottom: 1px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.borders.primary;
}};

  th {
    padding-bottom: 1em;
  }
`;
const NotMovingCard = _ref3 => {
  let {
    widget,
    dateFrom,
    dateTo,
    isEditable = false,
    ...rest
  } = _ref3;
  const {
    t
  } = useTranslation();
  const {
    items
  } = widget.data;
  const itemsPerPage = 3;
  const posthog = usePostHog();
  const [page, setPage] = useState(1);
  const [showDismissed, setShowDismissed] = React.useState(false);
  const filteredItems = items.filter(item => !item.isClosed && !item.isDismissed || showDismissed && item.isDismissed);
  const totalPageCount = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const followedTopics = useSelector(topicSelectors.getFollowedTopics);
  const handleNextPageClick = () => {
    setPage(prev => prev + 1);
    posthog.capture(`Insights Not Moving – Next Page Click`, {
      from: page
    });
  };
  const handlePrevPageClick = () => {
    setPage(prev => prev - 1);
    posthog.capture(`Insights Not Moving – Next Page Click`, {
      from: page
    });
  };
  const handleViewAll = () => {
    const params = new URLSearchParams();
    params.append("filter[types][]", InsightsLineItemItemTypeEnum.TODO);
    params.append("filter[statuses][]", InsightsLineItemItemStatusEnum.OPEN);
    params.append("filter[dateFrom]", moment(dateFrom).subtract(1, "year").toISOString());
    params.append("filter[dateTo]", moment(dateTo).subtract(1, "week").toISOString());
    followedTopics.forEach(topic => {
      params.append("filter[topicIds][]", topic.id.toString());
    });
    const url = `${window.location.origin}${RoutesEnum.PROGRESS_UPDATES}?${params.toString()}`;
    window.open(url, "_self");
  };
  return /*#__PURE__*/React.createElement(Card, rest, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto",
    gap: 2,
    align: "center"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    fontSize: 8,
    s: {
      mb: 3
    }
  }, t("NotMovingCard.whatsNotMoving"))), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    gap: 3
  }, filteredItems.length > itemsPerPage && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    onClick: handlePrevPageClick,
    color: "transparent",
    disabled: page === 1,
    s: {
      p: 1
    },
    outline: false,
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    display: "block",
    rotate: -90,
    src: "chevron"
  })), /*#__PURE__*/React.createElement(Text, {
    weight: "medium",
    color: "textSecondary"
  }, page, " of ", totalPageCount), /*#__PURE__*/React.createElement(Button, {
    onClick: handleNextPageClick,
    color: "transparent",
    disabled: totalPageCount <= 1 || page >= totalPageCount,
    s: {
      p: 1
    },
    outline: false,
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    display: "block",
    rotate: 90,
    src: "chevron"
  }))), isEditable && /*#__PURE__*/React.createElement(Dropdown, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.options"),
    display: "inline-block",
    direction: "bottomLeft",
    handlerProps: {
      color: showDismissed ? "translucentPrimary" : "translucent",
      s: {
        p: 2
      }
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      display: "block",
      src: "filter"
    })
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        onItemClick();
        setShowDismissed(prev => !prev);
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: showDismissed ? "checkboxFill" : "checkbox",
      s: {
        mr: 2
      },
      color: "primary"
    }), "Show Dismissed"));
  }), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("FindingsCard.viewAllActionItems"),
    onClick: handleViewAll,
    color: "translucent",
    s: {
      pl: 6,
      pr: 4,
      py: 2
    },
    fontSize: 3.5,
    elemAfter: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      s: {
        mt: -0.3,
        ml: 1
      }
    })
  }, t("common.actions.viewAll")))), filteredItems.length ? /*#__PURE__*/React.createElement(StyledTable, {
    size: "m",
    fontSize: 3.2,
    tableProps: {
      style: {
        minWidth: "1000px"
      }
    },
    s: {
      mx: -5,
      mb: -5
    }
  }, /*#__PURE__*/React.createElement(StyledTableHeader, null, /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Th, {
    width: "0"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    width: "180px"
  }, t("NotMovingCard.topic")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "250px"
  }, t("NotMovingCard.context")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "250px"
  }, t("NotMovingCard.action")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px"
  }, t("NotMovingCard.age")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px"
  }, t("NotMovingCard.owner")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px",
    center: true
  }, t("NotMovingCard.urgency")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "30px"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    width: "0"
  }))), /*#__PURE__*/React.createElement(Table.Body, null, paginatedItems.map(item => /*#__PURE__*/React.createElement(NotMovingTableRow, {
    showDismissed: showDismissed,
    isEditable: isEditable,
    key: item.id,
    item: item
  })))) : /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    justify: "center",
    align: "center",
    fullHeight: true
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "tick",
    color: "translucentSuccess"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 6
    },
    align: "center",
    style: {
      maxWidth: "300px"
    }
  }, t("NotMovingCard.everythingIsOnMove"))));
};
export default NotMovingCard;